import { useState, useRef, useEffect, useCallback } from 'react';
import { Pencil, X } from 'lucide-react';
import applyTheme, { getStoredTheme } from '../../utils/applyTheme';
import { listCustomThemes, deleteCustomTheme } from '../../utils/customThemes';
import { pushThemesToServer } from '../../utils/themeSync';
import useDropdownKeyboard from '../../hooks/useDropdownKeyboard';
import ThemeBuilderModal from '../ThemeBuilderModal/ThemeBuilderModal';
import styles from './ProfileHeader.module.css';

export default function ProfileHeader({ profile, title, isOwner, onRevealClick }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(getStoredTheme);
  const [customThemes, setCustomThemes] = useState(listCustomThemes);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingTheme, setEditingTheme] = useState(null);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const closeDropdown = useCallback(() => setShowDropdown(false), []);
  useDropdownKeyboard(menuRef, showDropdown, closeDropdown);

  useEffect(() => {
    if (!showDropdown) return;
    // Re-read on open in case another tab/component changed the stored themes.
    setCustomThemes(listCustomThemes());
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showDropdown]);

  const openBuilder = (theme) => {
    setEditingTheme(theme);
    setShowBuilder(true);
    setShowDropdown(false);
  };

  const handleDelete = (theme) => {
    deleteCustomTheme(theme.id);
    // Check the persisted selection (not component state, which can lag a
    // server pull): the fallback must land in localStorage BEFORE the push
    // reads it, or the push would send a dangling custom:<id> (server 400).
    if (getStoredTheme() === `custom:${theme.id}`) {
      applyTheme('dusk');
    }
    setCurrentTheme(getStoredTheme());
    setCustomThemes(listCustomThemes());
    pushThemesToServer();
  };

  return (
    <section className={styles.header}>
      <div className={styles.titleRow}>
        {title ? (
          <h1 className={styles.breadcrumb}>
            <span className={styles.name}>{title}</span>
          </h1>
        ) : (
          <h1 className={styles.breadcrumb}>
            <a href="/explore" className={styles.prefix}>EGO</a>
            <span className={styles.separator}> / </span>
            <span className={styles.name}>{profile?.name}</span>
          </h1>
        )}
      </div>
      <div className={styles.actions}>
        <div className={styles.moreWrapper} ref={dropdownRef}>
          <button
            className={styles.moreBtn}
            onClick={() => {
              // Re-read on open: a background sync pull may have changed the
              // active theme (and custom list) since mount.
              setCurrentTheme(getStoredTheme());
              setCustomThemes(listCustomThemes());
              setShowDropdown(p => !p);
            }}
            aria-haspopup="true"
            aria-expanded={showDropdown}
          >
            <span className={styles.moreBtnLabel}>
              More
              <svg width="8" height="5" viewBox="0 0 8 5" fill="currentColor">
                <path d="M0 0L4 5L8 0Z"/>
              </svg>
            </span>
            <span className={styles.moreBtnIcon}>···</span>
          </button>
          {showDropdown && (
            <div className={styles.dropdown} role="menu" aria-label="More options" ref={menuRef}>
              {!isOwner && onRevealClick && (
                <button
                  className={styles.revealBtn}
                  role="menuitem"
                  tabIndex={-1}
                  onClick={() => { setShowDropdown(false); onRevealClick(); }}
                >
                  Reveal hidden channels
                </button>
              )}
              <div className={styles.themePickerOnly}>
                <span className={styles.themeLabel}>Theme</span>
                <div className={styles.themePills}>
                  {['dusk', 'light', 'dawn'].map(t => (
                    <button
                      key={t}
                      className={`${styles.themePill} ${currentTheme === t ? styles.themePillActive : ''}`}
                      onClick={() => { applyTheme(t); setCurrentTheme(t); pushThemesToServer(); }}
                    >{t}</button>
                  ))}
                </div>
              </div>
              {customThemes.length > 0 && (
                <div className={styles.customThemeList}>
                  {customThemes.map(t => {
                    const selection = `custom:${t.id}`;
                    const isActive = currentTheme === selection;
                    return (
                      <div
                        key={t.id}
                        className={`${styles.customThemeRow} ${isActive ? styles.customThemeRowActive : ''}`}
                      >
                        <button
                          className={styles.customThemeSelect}
                          role="menuitem"
                          tabIndex={-1}
                          aria-label={`Use theme ${t.name}`}
                          onClick={() => { applyTheme(selection); setCurrentTheme(getStoredTheme()); pushThemesToServer(); }}
                        >
                          <span className={styles.swatches} aria-hidden="true">
                            <span className={styles.swatch} style={{ backgroundColor: t.bgHex }} />
                            <span className={styles.swatch} style={{ backgroundColor: t.accentHex }} />
                            <span className={styles.swatch} style={{ backgroundColor: t.textHex }} />
                          </span>
                          <span className={styles.customThemeName}>{t.name}</span>
                        </button>
                        <button
                          className={styles.customThemeAction}
                          role="menuitem"
                          tabIndex={-1}
                          aria-label={`Edit theme ${t.name}`}
                          onClick={() => openBuilder(t)}
                        >
                          <Pencil size={11} />
                        </button>
                        <button
                          className={styles.customThemeAction}
                          role="menuitem"
                          tabIndex={-1}
                          aria-label={`Delete theme ${t.name}`}
                          onClick={() => handleDelete(t)}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              <button
                className={styles.createThemeBtn}
                role="menuitem"
                tabIndex={-1}
                onClick={() => openBuilder(null)}
              >
                Create theme
                <span className={styles.createThemePlus}>&nbsp;+</span>
              </button>
            </div>
          )}
        </div>
      </div>
      {showBuilder && (
        <ThemeBuilderModal
          initial={editingTheme}
          onClose={() => setShowBuilder(false)}
          onSaved={(saved) => {
            setCustomThemes(listCustomThemes());
            setCurrentTheme(`custom:${saved.id}`);
          }}
        />
      )}
    </section>
  );
}
